import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";

import feedbackAPI from "./feedbackAPI";
import handleAPIError, {
  MESSAGE_CODES,
  showSuccessToast,
} from "../services/toast";
import styles from "./feedbackModal.module.css";

export const FEEDBACK_CATEGORIES = [
  "Add New Entity",
  "Add/Update Emission Factor Database",
  "Suggestion",
  "Compliment",
  "Bug",
  "Other",
];

const FeedbackTypeSelect = ({
  value,
  options,
  onChange,
  placeholder,
  disabled,
}) => {
  const { t } = useTranslation("UserMenu");
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const filteredOptions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase();
    if (!normalizedSearch) return options;

    return options.filter((option) =>
      t(option).toLocaleLowerCase().includes(normalizedSearch),
    );
  }, [options, searchTerm, t]);

  const selectedLabel = value ? t(value) : "";
  const inputValue = open ? searchTerm : selectedLabel;

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [searchTerm]);

  const openMenu = () => {
    if (disabled) return;
    setOpen(true);
    setSearchTerm("");
  };

  const selectOption = (option) => {
    onChange(option);
    setOpen(false);
    setSearchTerm("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setOpen(false);
      setSearchTerm("");
      return;
    }

    if (event.key === "Tab") {
      setOpen(false);
      setSearchTerm("");
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      setActiveIndex((index) =>
        Math.min(index + 1, filteredOptions.length - 1),
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      const option = filteredOptions[activeIndex];
      if (option) selectOption(option);
    }
  };

  return (
    <div className={styles.feedbackSelect} ref={rootRef}>
      <div
        className={`${styles.feedbackSelectControl} ${
          open ? styles.feedbackSelectControlOpen : ""
        }`}
      >
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="feedback-category-options"
          aria-autocomplete="list"
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus
          onFocus={openMenu}
          onChange={(event) => {
            setSearchTerm(event.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className={styles.feedbackSelectToggle}
          disabled={disabled}
          tabIndex="-1"
          aria-label={t(open ? "Close options" : "Open options")}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (open) {
              setOpen(false);
              setSearchTerm("");
            } else {
              openMenu();
              inputRef.current?.focus();
            }
          }}
        >
          <span
            className={`${styles.feedbackSelectChevron} ${
              open ? styles.feedbackSelectChevronOpen : ""
            }`}
            aria-hidden="true"
          />
        </button>
      </div>

      {open && (
        <div
          id="feedback-category-options"
          className={styles.feedbackSelectMenu}
          role="listbox"
        >
          {filteredOptions.length ? (
            filteredOptions.map((option, index) => {
              const selected = option === value;
              const active = index === activeIndex;

              return (
                <button
                  type="button"
                  key={option}
                  role="option"
                  aria-selected={selected}
                  className={`${styles.feedbackSelectOption} ${
                    selected ? styles.feedbackSelectOptionSelected : ""
                  } ${active ? styles.feedbackSelectOptionActive : ""}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectOption(option)}
                >
                  {t(option)}
                </button>
              );
            })
          ) : (
            <div className={styles.feedbackSelectEmpty}>
              {t("No related options")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FeedbackModal = ({ open, onClose }) => {
  const { t } = useTranslation("UserMenu");
  const [type, setType] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedMessage = message.trim();
    if (!type || !normalizedMessage || submitting) return;

    setSubmitting(true);
    try {
      const response = await feedbackAPI.submitFeedback({
        type,
        message: normalizedMessage,
      });

      showSuccessToast({
        apiData: response,
        scenarioCode: MESSAGE_CODES.FEEDBACK_SEND_SUCCESS,
      });
      setType("");
      setMessage("");
      onClose();
    } catch (error) {
      handleAPIError(error, {
        throwError: false
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-modal-title"
      >
        <header className={styles.header}>
          <div className={styles.titleGroup}>
            <span className={styles.titleIcon} aria-hidden="true">
              <SupportAgentOutlinedIcon />
            </span>
            <h2 id="feedback-modal-title">{t("Problem Feedback")}</h2>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("Close")}
          >
            <CloseIcon />
          </button>
        </header>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.field}>
            <span>{t("Category")}</span>
            <FeedbackTypeSelect
              value={type}
              options={FEEDBACK_CATEGORIES}
              onChange={setType}
              placeholder={t("Please select a category...")}
              disabled={submitting}
            />
          </div>

          <label className={styles.field}>
            <span>{t("Message")}</span>
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder={t(
                "Please describe the issue or feedback you would like to share...",
              )}
              rows="5"
            />
          </label>

          <button
            type="submit"
            className={styles.submitButton}
            disabled={!type || !message.trim() || submitting}
          >
            {t(submitting ? "Sending..." : "Send")}
          </button>

          <p className={styles.note}>
            {t(
              "After sending, the system will record this feedback and notify the NxMAP team.",
            )}
          </p>
        </form>
      </section>
    </div>,
    document.body,
  );
};

export default FeedbackModal;
